import * as React from 'react';
import clsx from 'clsx';
import { Theme, alpha } from '@mui/material/styles';
import { StyleRules, WithStyles, withStyles } from '@egr/xbox/utils/tss-react';

type ViewClasses = 'root' | 'transition' | 'hideByMove' | 'hideByVisibility';

export interface ViewProps {
    visible: boolean;
    color?: 'default' | 'primary' | 'grey' | 'dark' | 'transparent';
    transition?: boolean;
    keepMounted?: boolean;
    className?: string;
}

class ViewWithStyles extends React.PureComponent<WithStyles<ViewClasses> & ViewProps> {
    protected get className(): string | undefined {
        switch (this.props.color) {
            case 'transparent':
                return this.props.classes?.root;
            case 'primary':
            case 'dark':
            case 'grey':
                return `${this.props.classes?.root ?? ''} ${this.props.classes?.root ?? ''}-${this.props.color}`;
            default:
                return `${this.props.classes?.root ?? ''} ${this.props.classes?.root ?? ''}-default`;
        }
    }

    public override render(): React.ReactNode {
        if (!this.props.visible && !this.props.keepMounted) {
            return null;
        }

        return (
            <div
                className={clsx(
                    this.props.className,
                    this.className,
                    this.props.transition ? this.props.classes?.transition : undefined,
                    (this.props.transition && !this.props.visible) ? this.props.classes?.hideByMove : undefined,
                    (!this.props.transition && !this.props.visible) ? this.props.classes?.hideByVisibility : undefined,
                )}
                onClick={this.onClick}
            >
                {this.props.children}
            </div>
        );
    }

    protected onClick(): void {
        /* */
    }
}

function styles(theme: Theme): StyleRules<{}, ViewClasses> {
    return {
        transition: {
            transition: 'transform 330ms',
        },
        root: {
            alignItems: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            maxHeight: '100vh',
            width: '100vw',
            WebkitTapHighlightColor: 'rgba(0, 0, 0, 0)',
            position: 'absolute',
            top: 0,
            zIndex: 1,
            overflow: 'hidden',
            transform: 'translate3d(0, 0, 0)',
            '&-default': {
                backgroundColor: theme.palette.background.default,
            },
            '&-primary': {
                backgroundColor: alpha(theme.palette.primary.main, .3),
            },
            '&-grey': {
                backgroundColor: theme.palette.grey[300],
            },
            '&-dark': {
                backgroundColor: theme.palette.grey[500],
            },
        },
        hideByMove: {
            transform: 'translate3d(0, 100vh, 0)',
            zIndex: 0,
        },
        hideByVisibility: {
            visibility: 'hidden',
            zIndex: 0,
        }
    };
}

const View: React.ComponentType<ViewProps> = withStyles(ViewWithStyles, styles);

export default View;
